import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Slider, IconButton, CircularProgress,
  InputAdornment, Autocomplete,
} from '@mui/material';
import Map, { Marker, Source, Layer, type MapRef } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { createCommandCentre, updateCommandCentre, CommandCentrePayload } from '../../services/commandCentreService';
import { getStates, getCities } from '../../services/nodeService';

interface Props {
  open: boolean;
  initial?: any; // existing CC data for edit mode
  onClose: () => void;
  onSaved: (message?: string, updatedData?: CommandCentrePayload) => void;
}

const MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v11';

const createCircleGeoJSON = (center: [number, number], radiusMeters: number, points = 64) => {
  const coords: [number, number][] = [];
  const R = 6378137;
  const [lng, lat] = center;
  for (let i = 0; i < points; i++) {
    const angle = (i * 2 * Math.PI) / points;
    const dx = radiusMeters * Math.cos(angle);
    const dy = radiusMeters * Math.sin(angle);
    const dLat = dy / R;
    const dLng = dx / (R * Math.cos((lat * Math.PI) / 180));
    coords.push([lng + (dLng * 180) / Math.PI, lat + (dLat * 180) / Math.PI]);
  }
  coords.push(coords[0]);
  return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [coords] }, properties: {} } as any;
};

const CreateCommandCentreDrawer: React.FC<Props> = ({ open, initial, onClose, onSaved }) => {
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN ?? '';
  const isValidToken = Boolean(mapboxToken && mapboxToken.startsWith('pk.'));
  const mapRef = useRef<MapRef | null>(null);

  const [name, setName] = useState(initial?.name ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [radius, setRadius] = useState<number>((initial?.radius ?? 500) / 1000);
  const [coords, setCoords] = useState<{ lat: number; lng: number }>({
    lat: initial?.latitude ?? 51.51,
    lng: initial?.longitude ?? -0.134,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [stateList, setStateList] = useState<Array<{ id: number; name: string }>>([]);
  const [citiesList, setCitiesList] = useState<Array<{ id: number; name: string }>>([]);

  // Reset fields when drawer opens with new initial data
  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setCity(initial?.city ?? '');
      setState(initial?.state ?? '');
      setAddress(initial?.address ?? '');
      setRadius((initial?.radius ?? 500) / 1000);
      setCoords({ lat: initial?.latitude ?? 51.51, lng: initial?.longitude ?? -0.134 });
      setFormError(null);
      setSearchQuery('');
      setSuggestions([]);
      getStates().then(setStateList).catch(() => {});
      getCities().then(setCitiesList).catch(() => {});
    }
  }, [open, initial]);

  // Auto-detect current location for new CC
  useEffect(() => {
    if (open && !initial && !initial?.latitude) {
      const stored = localStorage.getItem('wualt:lastLocation');
      if (stored) {
        try {
          const { lat, lng } = JSON.parse(stored);
          if (typeof lat === 'number' && typeof lng === 'number') {
            setCoords({ lat, lng });
            return;
          }
        } catch {}
      }
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const lat = pos.coords.latitude;
            const lng = pos.coords.longitude;
            setCoords({ lat, lng });
            localStorage.setItem('wualt:lastLocation', JSON.stringify({ lat, lng }));
          },
          () => {}
        );
      }
    }
  }, [open]);

  const flyTo = useCallback((lat: number, lng: number) => {
    try {
      const map = mapRef.current?.getMap?.();
      if (map && typeof map.flyTo === 'function') map.flyTo({ center: [lng, lat], zoom: 14 });
    } catch {}
  }, []);

  // Debounced geocoding suggestions
  useEffect(() => {
    if (!isValidToken || !searchQuery || searchQuery.length < 2) {
      setSuggestions([]); setShowSuggestions(false); return;
    }
    const t = setTimeout(async () => {
      try {
        const q = encodeURIComponent(searchQuery.trim());
        const host = process.env.REACT_APP_MAPBOX_HOST ?? 'https://api.mapbox.com';
        const res = await fetch(`${host}/geocoding/v5/mapbox.places/${q}.json?access_token=${mapboxToken}&autocomplete=true&limit=5`);
        if (!res.ok) { setSuggestions([]); setShowSuggestions(true); return; }
        const body = await res.json();
        setSuggestions(Array.isArray(body?.features) ? body.features : []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]); setShowSuggestions(true);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, mapboxToken, isValidToken]);

  // Hide suggestions on outside click
  useEffect(() => {
    const onDocClick = (ev: MouseEvent) => {
      if (!searchBoxRef.current) return;
      if (!(ev.target instanceof Node)) return;
      if (!searchBoxRef.current.contains(ev.target)) setShowSuggestions(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const circleFeature = React.useMemo(
    () => createCircleGeoJSON([coords.lng, coords.lat], radius * 1000),
    [coords.lng, coords.lat, radius]
  );

  const isFormValid = () => Boolean(name.trim());

  const handleSave = async () => {
    if (!isFormValid()) { setFormError('Name is required'); return; }
    if (!radius || radius < 0.05 || radius > 100) { setFormError('Radius must be between 0.05 km and 100 km'); return; }
    setSaving(true);
    setFormError(null);
    const payload: CommandCentrePayload = {
      name: name.trim(),
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      address: address.trim() || undefined,
      latitude: coords.lat,
      longitude: coords.lng,
      radius: Math.round(radius * 1000),
    };
    try {
      if (initial?.id) {
        await updateCommandCentre(initial.id, payload);
        window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Command Centre updated successfully' } }));
        onSaved('Command Centre updated successfully', payload);
      } else {
        await createCommandCentre(payload);
        window.dispatchEvent(new CustomEvent('wualt:notification', { detail: { message: 'Command Centre created successfully' } }));
        onSaved('Command Centre created successfully', payload);
      }
    } catch (err: any) {
      setFormError(err?.message ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const labelSx = { fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', lineHeight: '20px', color: '#374151', mb: 0.5 };
  const inputSx = { '& .MuiInputBase-root': { height: '41.5px', borderRadius: '10px', paddingTop: '10px', paddingBottom: '10px', paddingLeft: '12px', paddingRight: '12px' } };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 480, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '24px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
        <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 600, fontSize: '16px', lineHeight: '24px', letterSpacing: '-0.31px' }}>
          {initial ? 'Edit Command Centre' : 'Add Command Centre'}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ width: 16, height: 16, minWidth: 16, padding: 0 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: '24px', py: '20px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {formError && (
          <Box sx={{ borderRadius: '8px', backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: '50%', backgroundColor: '#DE606C', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1, flexShrink: 0 }}>
              <svg width="10" height="10" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" /></svg>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', color: '#374151', flex: 1 }}>{formError}</Typography>
            <Box component="span" onClick={() => setFormError(null)} sx={{ cursor: 'pointer', color: '#9CA3AF', fontSize: 18, ml: 1 }}>×</Box>
          </Box>
        )}

        {/* Name */}
        <Box>
          <Typography sx={labelSx}>Name *</Typography>
          <TextField fullWidth size="small" placeholder="e.g. London Central" value={name} onChange={(e) => setName(e.target.value)} sx={inputSx} />
        </Box>

        {/* City + State */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>City</Typography>
            <Autocomplete
              options={citiesList}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
              value={citiesList.find((c) => c.name === city) ?? null}
              onChange={(_e, val) => setCity(val?.name ?? '')}
              size="small"
              renderInput={(params) => (
                <TextField {...params} placeholder="Select city" sx={inputSx} />
              )}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>State / Region</Typography>
            <Autocomplete
              options={stateList}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
              value={stateList.find((s) => s.name === state) ?? null}
              onChange={(_e, val) => setState(val?.name ?? '')}
              size="small"
              renderInput={(params) => (
                <TextField {...params} placeholder="Select state" sx={inputSx} />
              )}
            />
          </Box>
        </Box>

        {/* Address */}
        <Box>
          <Typography sx={labelSx}>Address</Typography>
          <TextField fullWidth size="small" placeholder="Street address" value={address} onChange={(e) => setAddress(e.target.value)} sx={inputSx} />
        </Box>

        {/* Map search + map */}
        <Box>
          <Typography sx={labelSx}>Location on Map</Typography>
          {/* Search box */}
          <Box ref={searchBoxRef} sx={{ position: 'relative', mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search location…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={inputSx}
            />
            {showSuggestions && suggestions.length > 0 && (
              <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, backgroundColor: '#FFFFFF', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', mt: '4px', overflow: 'hidden' }}>
                {suggestions.map((feat: any) => (
                  <Box
                    key={feat.id}
                    onClick={() => {
                      const [lng, lat] = feat.center || [null, null];
                      if (lng != null && lat != null) {
                        setCoords({ lat, lng });
                        flyTo(lat, lng);
                        // auto-fill address from feature
                        if (feat.place_name) setAddress(feat.place_name);
                        // try to extract city and state
                        const ctx = feat.context || [];
                        for (const c of ctx) {
                          if ((c?.id ?? '').startsWith('place')) {
                            const text = c.text ?? '';
                            const match = citiesList.find((x) => x.name.toLowerCase() === text.toLowerCase());
                            if (match) setCity(match.name);
                          }
                          if ((c?.id ?? '').startsWith('region')) {
                            const text = c.text ?? '';
                            const match = stateList.find((x) => x.name.toLowerCase() === text.toLowerCase());
                            if (match) setState(match.name);
                          }
                        }
                      }
                      setSearchQuery(feat.place_name ?? searchQuery);
                      setShowSuggestions(false);
                    }}
                    sx={{ px: 2, py: 1, cursor: 'pointer', fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', '&:hover': { backgroundColor: '#F9FAFB' } }}
                  >
                    {feat.place_name}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Map */}
          <Box sx={{ width: '100%', height: '220px', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            {isValidToken ? (
              <Map
                ref={mapRef}
                mapboxAccessToken={mapboxToken}
                mapStyle={MAPBOX_STYLE}
                initialViewState={{ longitude: coords.lng, latitude: coords.lat, zoom: 12 }}
                style={{ width: '100%', height: '100%' }}
                onClick={(e) => {
                  setCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng });
                }}
              >
                <Source type="geojson" data={circleFeature}>
                  <Layer type="fill" paint={{ 'fill-color': '#39A9B1', 'fill-opacity': 0.12 }} />
                  <Layer type="line" paint={{ 'line-color': '#39A9B1', 'line-width': 1.5, 'line-dasharray': [4, 3] }} />
                </Source>
                <Marker
                  longitude={coords.lng}
                  latitude={coords.lat}
                  anchor="bottom"
                  draggable
                  onDragEnd={(e: any) => setCoords({ lat: e.lngLat.lat, lng: e.lngLat.lng })}
                >
                  <svg width="24" height="32" viewBox="0 0 24 32" fill="none">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20S24 21 24 12C24 5.373 18.627 0 12 0z" fill="#39A9B1" />
                    <circle cx="12" cy="12" r="5" fill="#FFFFFF" />
                  </svg>
                </Marker>
              </Map>
            ) : (
              <Box sx={{ width: '100%', height: '100%', backgroundColor: '#F0F9F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: '#6B7280' }}>Map unavailable — set REACT_APP_MAPBOX_TOKEN</Typography>
              </Box>
            )}
          </Box>

          {/* Coords badge */}
          <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '11px', color: '#9CA3AF', mt: 0.5 }}>
            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)} · radius {radius} km — click map or drag pin to reposition
          </Typography>
        </Box>

        {/* Radius */}
        <Box>
          <Typography sx={labelSx}>Geofence Radius (km)</Typography>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: 12, color: '#6B7280', flexShrink: 0 }}>0.05 km</Typography>
              <Slider
                value={Math.min(100, Math.max(0.05, radius || 0.05))}
                min={0.05}
                max={100}
                step={0.05}
                onChange={(_e, v) => setRadius(Array.isArray(v) ? v[0] : v)}
                sx={{ flex: 1, color: '#39A9B1' }}
              />
              <Typography variant="body2" sx={{ fontSize: 12, color: '#6B7280', flexShrink: 0 }}>100 km</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
              <TextField
                size="small"
                type="number"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                slotProps={{ htmlInput: { step: 0.05 }, input: { endAdornment: <InputAdornment position="end"><Typography variant="caption">km</Typography></InputAdornment> } }}
                sx={{ width: 100, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 34 }, '& input': { textAlign: 'center', px: 1 } }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: '24px', py: '16px', borderTop: '1px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'flex-end', gap: 2, flexShrink: 0 }}>
        <Button
          onClick={onClose}
          sx={{ height: '36px', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'transparent', color: '#374151', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!isFormValid() || saving}
          sx={{ height: '36px', borderRadius: '10px', backgroundColor: '#39A9B1', color: '#FFFFFF', textTransform: 'none', fontFamily: 'Inter, Arial, sans-serif', fontWeight: 500, fontSize: '13px', px: 2, '&:hover': { backgroundColor: '#2d8f96' }, '&.Mui-disabled': { backgroundColor: '#39A9B1', opacity: 0.5, color: '#FFFFFF' } }}
        >
          {saving ? <CircularProgress size={14} sx={{ color: '#FFFFFF' }} /> : (initial ? 'Update' : 'Create')}
        </Button>
      </Box>
    </Drawer>
  );
};

export default CreateCommandCentreDrawer;
